import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import { Sparkles, HandHeart, Megaphone, Compass, Heart, Users } from "lucide-react";
import { CORE_VALUES, ABOUT_BLOCKS } from "../data/mock";
import { PHOTOS } from "../data/photos";
import { ArrowRight } from "lucide-react";

const ICONS = { Sparkles, HandHeart, Megaphone, Compass };

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="About Us"
          title="Together we recover."
          description="Recovery Connections of Central Florida is a Recovery Community Organization (RCO) — an independent 501(c)(3) nonprofit led and governed by local recovery community representatives."
          breadcrumbs={[{ label: "About Us" }]}
          image={PHOTOS.meeting4}
        />

        {/* Mission */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                Our Mission
              </p>
              <h2 className="font-serif-display mt-3 text-[36px] md:text-[44px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                Empowering recovery through personalized, peer-led guidance.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-[16.5px] leading-[1.8] text-[hsl(var(--rc-slate))]">
              <p>
                With <strong className="text-[hsl(var(--rc-deep))]">lived experience</strong> in recovery and empathetic compassion, we are committed to connecting individuals and families to resources and support that offer multiple pathways to long-term, sustainable recovery from substance use disorder.
              </p>
              <p>
                As a nonprofit Recovery Community Organization, our work is rooted in the conviction that recovery happens in community. Our team of Certified Recovery Peer Specialists doesn’t treat people — they walk alongside them, every step of the way.
              </p>
              <p>
                We believe everyone deserves the chance to be seen, heard, and supported by someone who has been there.
              </p>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 md:py-24 rc-section-soft">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="max-w-2xl">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                Our Core Values
              </p>
              <h2 className="font-serif-display mt-3 text-[36px] md:text-[44px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                The principles that guide every connection we make.
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {CORE_VALUES.map((v, i) => {
                const Icon = ICONS[v.icon] || Sparkles;
                return (
                  <article
                    key={v.title}
                    className="rc-card-hover bg-white border border-[hsl(var(--border))] rounded-2xl p-7 flex flex-col"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center mb-5">
                      <Icon className="w-5 h-5 text-[hsl(var(--rc-purple))]" />
                    </div>
                    <h3 className="font-serif-display text-[22px] font-semibold text-[hsl(var(--rc-deep))]">
                      {v.title}
                    </h3>
                    <p className="mt-3 text-[14.5px] leading-[1.65] text-[hsl(var(--rc-slate))] flex-1">
                      {v.description}
                    </p>
                    <div className="mt-6 text-[12px] tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">
                      0{i + 1}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-10">
            <div className="bg-[hsl(var(--rc-deep))] text-white rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[hsl(var(--rc-purple))]/40 blur-3xl" />
              <div className="relative">
                <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold">
                  Our Vision
                </p>
                <h3 className="font-serif-display mt-3 text-[30px] md:text-[36px] leading-[1.15] font-semibold">
                  A Central Florida where everyone seeking recovery has access to the support they need.
                </h3>
                <p className="mt-5 text-[15.5px] leading-[1.75] text-white/80">
                  We envision a community where peer-led recovery support is accessible, respected, and embedded across the systems people rely on — from healthcare to housing to the workplace.
                </p>
              </div>
            </div>
            <div className="bg-[hsl(var(--rc-paper))] rounded-2xl border border-[hsl(var(--border))] p-8 md:p-10">
              <Heart className="w-7 h-7 text-[hsl(var(--rc-purple))]" />
              <h3 className="font-serif-display mt-4 text-[30px] md:text-[36px] leading-[1.15] font-semibold text-[hsl(var(--rc-deep))]">
                What it means to be peer-led.
              </h3>
              <p className="mt-5 text-[15.5px] leading-[1.75] text-[hsl(var(--rc-slate))]">
                51% of our Board of Directors and the majority of our staff self-identify as people in recovery. Our work is shaped by lived experience — not in theory, but in daily practice.
              </p>
              <a href="/leadership" className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-[hsl(var(--rc-purple))] hover:text-[hsl(var(--rc-deep))] transition-colors group">
                Meet the team
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </section>

        {/* About blocks */}
        <section className="py-20 md:py-24 rc-section-soft">
          <div className="max-w-7xl mx-auto px-5 md:px-8 space-y-24">
            {ABOUT_BLOCKS.map((b, idx) => {
              const isLeft = b.align === "left";
              return (
                <div key={b.id} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                  <div className={`lg:col-span-6 ${isLeft ? "" : "lg:order-2"}`}>
                    <div className="relative aspect-[5/4] w-full overflow-hidden rounded-2xl">
                      <img src={b.image} alt={b.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]" loading="lazy" />
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${isLeft ? "" : "lg:order-1"}`}>
                    <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                      {b.eyebrow}
                    </p>
                    <h3 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                      {b.title}
                    </h3>
                    <p className="mt-5 text-[16px] leading-[1.75] text-[hsl(var(--rc-slate))]">
                      {b.body}
                    </p>
                    <a href={b.cta.href} className="mt-7 inline-flex items-center gap-2 text-[14px] font-semibold text-[hsl(var(--rc-deep))] hover:text-[hsl(var(--rc-purple))] transition-colors group">
                      {b.cta.label}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <CTABand
          eyebrow="Join the movement"
          title="Be part of building recovery in Central Florida."
          body="Whether you’re seeking support, a partner, or a friend of the recovery community — there’s a place for you here."
          primary={{ label: "See our programs", href: "/programs" }}
          secondary={{ label: "Get in touch", href: "/contact" }}
        />
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
